/**
 * Maps a failed API response (DRF validation errors) to a flat
 * { fieldPath: message } map so forms can render each error directly
 * under the field that caused it, instead of only as a generic toast.
 *
 * Handles both call-site shapes used across the app:
 *  - axios errors, where the body lives at `error.response.data`
 *  - RTK Query `FetchBaseQueryError`, where the body lives at `error.data`
 *
 * And both backend error shapes:
 *  - flat: {"asset_tag": ["This Asset Tag already exists."]}
 *  - nested/array subforms: {"warranties": [{}, {"warranty_from": ["..."]}]}
 *    -> flattened to "warranties[1].warranty_from"
 *  - the legacy stringified `{"detail": "{'asset_tag': [ErrorDetail(...)]}"}`
 *    shape some endpoints still emit
 */

interface BackendErrorResponse {
  detail?: string;
  error_type?: string;
  status_code?: number;
  non_field_errors?: unknown;
  [field: string]: unknown;
}

export interface ParsedApiErrors {
  /** field path -> message, e.g. "asset_tag", "warranties[0].warranty_from" */
  fieldErrors: Record<string, string>;
  /** generic message (non_field_errors / detail) to fall back to when nothing field-specific applies */
  message?: string;
}

// "traceback" shows up on the generic {"detail": "...", "traceback": "..."}
// shape the backend's exception handler emits for unhandled errors (e.g. a
// raw DB IntegrityError with no field-level validation) - it must NOT be
// treated as a field error, or forms wrongly think they got a specific
// field error and suppress their own generic fallback message, going silent.
// "__all__" is Django's own non-field-errors key, surfaced when a model-level
// constraint (e.g. a UniqueConstraint) is hit directly at the DB layer
// instead of being pre-empted by serializer validation - treat it like
// non_field_errors, not a field named "__all__".
const IGNORED_KEYS = new Set(['detail', 'error_type', 'status_code', 'non_field_errors', 'traceback', '__all__']);

function firstMessage(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.length > 0) {
    return typeof value[0] === 'string' ? value[0] : undefined;
  }
  return undefined;
}

function flatten(value: unknown, path: string, out: Record<string, string>): void {
  if (value == null) return;

  if (Array.isArray(value)) {
    if (value.length === 0) return;
    // Array of message strings - this IS the field's error, e.g. {"asset_tag": ["msg"]}
    if (typeof value[0] === 'string') {
      const msg = firstMessage(value);
      if (msg) out[path] = msg;
      return;
    }
    // Array of per-item objects - a subform/array field, e.g. {"warranties": [{}, {"warranty_from": [...]}]}
    value.forEach((item, index) => {
      if (item && typeof item === 'object' && Object.keys(item).length > 0) {
        flatten(item, `${path}[${index}]`, out);
      }
    });
    return;
  }

  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, v]) => {
      flatten(v, path ? `${path}.${key}` : key, out);
    });
    return;
  }

  if (typeof value === 'string') {
    out[path] = value;
  }
}

/** Parses an already-extracted DRF error response body. */
export function parseApiFieldErrors(data: unknown): ParsedApiErrors {
  const fieldErrors: Record<string, string> = {};

  if (!data || typeof data !== 'object') {
    return { fieldErrors, message: typeof data === 'string' ? data : undefined };
  }

  const body = data as BackendErrorResponse;
  let message: string | undefined;

  if (typeof body.detail === 'string') {
    if (body.detail.includes('ErrorDetail')) {
      try {
        const normalized = body.detail
          .replace(/ErrorDetail\(string='([^']+)', code='[^']+'\)/g, '"$1"')
          .replace(/'/g, '"');
        flatten(JSON.parse(normalized), '', fieldErrors);
      } catch {
        message = body.detail;
      }
    } else {
      message = body.detail;
    }
  }

  const nonFieldMessage = firstMessage(body.non_field_errors) || firstMessage(body.__all__);
  if (nonFieldMessage) message = nonFieldMessage;

  Object.entries(body).forEach(([key, value]) => {
    if (IGNORED_KEYS.has(key)) return;
    flatten(value, key, fieldErrors);
  });

  if (!message && Object.keys(fieldErrors).length === 0 && typeof body.detail === 'string') {
    message = body.detail;
  }

  return { fieldErrors, message };
}

/**
 * Extracts the response body from either an axios error or an RTK Query
 * error, then parses it. Safe to call with anything caught in a form's
 * submit `catch` block.
 */
export function parseApiError(error: unknown): ParsedApiErrors {
  if (error && typeof error === 'object') {
    if ('response' in error) {
      const data = (error as { response?: { data?: unknown } }).response?.data;
      if (data !== undefined) return parseApiFieldErrors(data);
    }
    if ('data' in error) {
      const data = (error as { data?: unknown }).data;
      if (data !== undefined) return parseApiFieldErrors(data);
    }
  }
  return { fieldErrors: {} };
}
